import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import StudentFormsModal from './StudentFormsModal';
import { formatDateOnly } from '../../utils/dateOnly';
import '../superadmin/DistrictsList.css';
import './ViewStudents.css';

/**
 * Grades sort in school order, not alphabetically: the values on file are "1".."12" plus
 * "Kindergarten" and "Preschool", and sorting those as text puts 10, 11 and 12 between 1
 * and 2. Blanks sort last either way, rather than leading the list.
 *
 * Module scope on purpose: these are pure, and declaring them inside the component put
 * them below the useMemo that uses them, which threw at runtime while still compiling.
 */
const gradeRank = (raw) => {
  const g = String(raw ?? '').trim().toLowerCase();
  if (!g) return 9999;
  if (g.startsWith('pre')) return -2;
  if (g.startsWith('k')) return -1;
  const n = parseInt(g, 10);
  return Number.isNaN(n) ? 9998 : n;
};

// Ordered by how much attention the student needs, so one click surfaces the backlog.
const STATUS_RANK = { none: 0, partial: 1, completed: 2 };

const ViewStudents = () => {
  const navigate = useNavigate();
  /*
   * Which columns exist, and which of them can be printed.
   *
   * The table used to hardcode its cells, with a colSpan literal that had to be kept in step by
   * hand. Driving both the header and the body from one list is what makes the column picker and
   * the print selection possible without writing the row out twice.
   *
   * Parent Email and Parent Phone are off by default: they are what the office needs to CHASE a
   * family, not what it needs to read down a list, and they make the table wide.
   */
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showFormsModal, setShowFormsModal] = useState(false);
  const [formsModalStudent, setFormsModalStudent] = useState(null);
  const [yearFilter, setYearFilter] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | completed | partial | none
  const [sortKey, setSortKey] = useState('name'); // name | grade | dob | status
  const [sortDir, setSortDir] = useState('asc');
  /* Remembered per browser, because it is a per-person preference and losing it is harmless.
     Every read and write is guarded: private windows and cleared site data both throw. */
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const raw = localStorage.getItem('vs_hidden_columns');
      return raw ? new Set(JSON.parse(raw)) : new Set(['email', 'phone']);
    } catch (e) { return new Set(['email', 'phone']); }
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showPrintPicker, setShowPrintPicker] = useState(false);
  const [printColumns, setPrintColumns] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [districtYear, setDistrictYear] = useState(null);
  const [districtName, setDistrictName] = useState('');
  const [yearDefaulted, setYearDefaulted] = useState(false);

  useEffect(() => {
    fetchStudents();
    // The district's active enrollment year, so the list opens on the year being worked
    // rather than on every year ever enrolled.
    axios.get('/api/districts/current')
      .then((res) => {
        const d = res.data.district || res.data || {};
        setDistrictYear(d.active_enrollment_school_year || null);
        // Only used on the printed sheet, which has no other way of saying whose list it is.
        setDistrictName(d.district_name || '');
      })
      .catch(() => setDistrictYear(null)); // fall back to the newest year in the data
  }, []);

  const yearOptions = useMemo(() => {
    const set = new Set(
      students.map((s) => s.enrollment_school_year).filter((y) => y != null && String(y).trim() !== '')
    );
    return Array.from(set).sort();
  }, [students]);

  /**
   * Open on the current school year. Prefer the district's configured active year; if it
   * is unset or has no students yet, use the newest year that actually has records, so
   * the screen never opens on an empty list. Applied once, so a staff member switching to
   * "All years" is not overridden when data reloads.
   */
  useEffect(() => {
    if (yearDefaulted || yearOptions.length === 0) return;
    const preferred = districtYear && yearOptions.includes(districtYear)
      ? districtYear
      : yearOptions[yearOptions.length - 1];
    setYearFilter(preferred);
    setYearDefaulted(true);
  }, [yearOptions, districtYear, yearDefaulted]);

  // Year first, so the counts on the status tiles describe the year being looked at.
  const inYear = useMemo(() => {
    if (!yearFilter) return students;
    return students.filter((s) => s.enrollment_school_year === yearFilter);
  }, [students, yearFilter]);

  const counts = useMemo(() => ({
    all: inYear.length,
    completed: inYear.filter((s) => s.form_status === 'completed').length,
    partial: inYear.filter((s) => s.form_status === 'partial').length,
    none: inYear.filter((s) => s.form_status === 'none').length
  }), [inYear]);

  const displayedStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = inYear.filter((s) => {
      if (statusFilter !== 'all' && s.form_status !== statusFilter) return false;
      if (!q) return true;
      // Parent name included: staff are often given the parent's name on the phone.
      return `${s.last_name} ${s.first_name} ${s.parent_first_name || ''} ${s.parent_last_name || ''}`
        .toLowerCase().includes(q);
    });

    const name = (s) => `${s.last_name || ''} ${s.first_name || ''}`.trim().toLowerCase();
    const compare = (a, b) => {
      switch (sortKey) {
        case 'grade': {
          const d = gradeRank(a.grade) - gradeRank(b.grade);
          return d !== 0 ? d : name(a).localeCompare(name(b));
        }
        case 'dob': {
          // Compare the YYYY-MM-DD text, never a Date: these are bare days with no
          // timezone, and building a Date shifts them for anyone west of UTC.
          const av = String(a.date_of_birth || '').slice(0, 10);
          const bv = String(b.date_of_birth || '').slice(0, 10);
          if (av === bv) return name(a).localeCompare(name(b));
          if (!av) return 1;   // unknown dates sort last
          if (!bv) return -1;
          return av < bv ? -1 : 1;
        }
        case 'status': {
          const d = (STATUS_RANK[a.form_status] ?? 3) - (STATUS_RANK[b.form_status] ?? 3);
          return d !== 0 ? d : name(a).localeCompare(name(b));
        }
        default:
          return name(a).localeCompare(name(b));
      }
    };

    const sorted = [...filtered].sort(compare);
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [inYear, search, statusFilter, sortKey, sortDir]);

  const STATUS_LABELS = {
    completed: 'Forms complete',
    partial: 'In progress',
    none: 'Not started'
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  /* Rendered for whichever columns are on. 'actions' is never printed - buttons on paper. */
  const COLUMNS = useMemo(() => ([
    { key: 'name', label: 'Name (Last, First)', sortKeyName: 'name', always: true,
      /* Plain text when printing. districtadmin/PDFView.css carries a bare `button` selector
         inside @media print, and CRA bundles CSS globally, so every button on every printed
         page is display:none - which blanked this entire column. Rendering text instead is the
         robust half of the fix; the CSS below re-shows it for a browser-native Ctrl+P. */
      cell: (st) => printing ? `${st.last_name}, ${st.first_name}` : (
        <button
          type="button"
          className="vs-name"
          onClick={() => handleViewStudent(st.id)}
          title={`View intake details for ${st.first_name} ${st.last_name} \u2014 date of birth, age, parent contact and enrollment year`}
          aria-label={`View intake details for ${st.first_name} ${st.last_name}`}
        >
          {st.last_name}, {st.first_name}
        </button>
      ) },
    { key: 'year', label: 'School year', onlyWhenAllYears: true,
      cell: (st) => st.enrollment_school_year || '\u2014' },
    /* Abbreviated on paper only. "Kindergarten" is the one grade label wide enough to wrap over
       two lines, and the column it needs to be that wide can only take the width from the date
       beside it. The grades in the data are 1-12, Kindergarten and Preschool. */
    { key: 'grade', label: 'Grade', sortKeyName: 'grade',
      cell: (st) => {
        const g = st.grade || '';
        if (!printing) return g || '\u2014';
        if (g === 'Kindergarten') return 'K';
        if (g === 'Preschool') return 'Pre';
        return g || '\u2014';
      } },
    { key: 'dob', label: 'Date of Birth', sortKeyName: 'dob',
      cell: (st) => formatDateOnly(st.date_of_birth, { placeholder: '-' }) },
    { key: 'parent', label: 'Parent',
      cell: (st) => `${st.parent_first_name || ''} ${st.parent_last_name || ''}`.trim() || '\u2014' },
    { key: 'email', label: 'Parent Email', defaultOff: true,
      cell: (st) => st.parent_email || '\u2014' },
    { key: 'phone', label: 'Parent Phone', defaultOff: true,
      cell: (st) => st.parent_phone || '\u2014' },
    { key: 'forms', label: 'Forms', cell: (st) => st.forms_submitted_count ?? 0 },
    { key: 'status', label: 'Status', sortKeyName: 'status',
      cell: (st) => (
        <span className={`vs-status vs-status-${st.form_status || 'none'}`}>
          {STATUS_LABELS[st.form_status] || 'Not started'}
        </span>
      ) },
    { key: 'actions', label: 'Actions', always: true, printable: false,
      cellClassName: 'actions vs-actions', headerClassName: 'vs-actions-head',
      cell: (st) => (
        <>
          <button
            onClick={() => { setFormsModalStudent(st); setShowFormsModal(true); }}
            className="btn-edit"
          >
            View Forms
          </button>
          <button
            onClick={() => navigate(`/district-admin/edit-student/${st.id}`)}
            className="btn-secondary"
          >
            Edit Forms
          </button>
          {/* Deliberately quiet. This used to be a filled red button repeated on
              every row, which made the most destructive action the loudest thing
              on the page and put it a few pixels from "Edit Forms". The red is
              kept for the confirmation dialog, where it means something. */}
          <button
            onClick={() => handleSoftDelete(st.id)}
            className="vs-delete"
            title={`Delete ${st.first_name} ${st.last_name}'s record`}
          >
            Delete
          </button>
        </>
      ) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [sortKey, sortDir, yearFilter, printing]);

  useEffect(() => {
    try { localStorage.setItem('vs_hidden_columns', JSON.stringify([...hiddenColumns])); }
    catch (e) { /* private mode: the preference just does not persist */ }
  }, [hiddenColumns]);

  /*
   * Printing renders the chosen columns, prints, then puts the screen back.
   *
   * Driving it through state rather than print-only CSS is what lets the print selection differ
   * from what is on screen - she can keep email and phone off the table and still put them on the
   * sheet she is going to work from. onafterprint restores even if the dialog is cancelled.
   */
  useEffect(() => {
    if (!printing) return undefined;
    const restore = () => setPrinting(false);
    window.addEventListener('afterprint', restore);
    const id = setTimeout(() => window.print(), 50);
    return () => { window.removeEventListener('afterprint', restore); clearTimeout(id); };
  }, [printing]);

  const columnIsAvailable = (col) => !(col.onlyWhenAllYears && yearFilter);
  const activeColumns = COLUMNS.filter((col) => {
    if (!columnIsAvailable(col)) return false;
    if (printing) return col.printable !== false && (printColumns || new Set()).has(col.key);
    return col.always || !hiddenColumns.has(col.key);
  });

  const toggleColumn = (key) => setHiddenColumns((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const openPrintPicker = () => {
    setPrintColumns(new Set(
      COLUMNS.filter((c) => columnIsAvailable(c) && c.printable !== false
        && (c.always || !hiddenColumns.has(c.key))).map((c) => c.key)
    ));
    setShowPrintPicker(true);
  };

  const togglePrintColumn = (key) => setPrintColumns((prev) => {
    const next = new Set(prev || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const SortableHeader = ({ label, sortKeyName, columnKey }) => {
    const active = sortKey === sortKeyName;
    return (
      <th className={columnKey ? `vs-col-${columnKey}` : undefined} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button
          type="button"
          className={`vs-sort ${active ? 'is-active' : ''}`}
          onClick={() => toggleSort(sortKeyName)}
          title={`Sort by ${label.toLowerCase()}`}
        >
          {label}
          <span className="vs-sort-caret" aria-hidden="true">
            {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
          </span>
        </button>
      </th>
    );
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/student-intake/district');
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = async (studentId) => {
    try {
      const response = await axios.get(`/api/student-intake/district/${studentId}`);
      setSelectedStudent(response.data);
    } catch (error) {
      console.error('Failed to fetch student details:', error);
      alert('Failed to load student details');
    }
  };

  const handleSoftDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student record? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/student-intake/district/${studentId}`);
      // Refresh the list
      fetchStudents();
      setSelectedStudent(null);
    } catch (error) {
      console.error('Failed to delete student:', error);
      alert('Failed to delete student record');
    }
  };

  const renderStudentDetails = (student) => {
    return (
      <div>
        <h4>Student Information:</h4>
        <div style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '4px' }}>
          <p><strong>Name:</strong> {student.last_name}, {student.first_name}</p>
          <p><strong>Date of Birth:</strong> {formatDateOnly(student.date_of_birth, { placeholder: 'Not provided' })}</p>
          <p><strong>Age:</strong> {student.age || 'Not calculated'}</p>
          {/* Last 4 SSN is deliberately not shown. It is on the record and is needed for
              SIS entry, but this panel is for identifying a student at a glance and does
              not need to put part of their SSN on screen to do that. */}
          <p><strong>Parent:</strong> {student.parent_first_name} {student.parent_last_name}</p>
          <p><strong>Parent Email:</strong> {student.parent_email}</p>
          <p><strong>Enrollment school year:</strong> {student.enrollment_school_year || '—'}</p>
          <p><strong>Created:</strong> {new Date(student.created_at).toLocaleString()}</p>
        </div>
      </div>
    );
  };

  if (loading && students.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>Students</h2>
        <p style={{ fontSize: '0.9em', color: '#666', marginTop: '0.5rem' }}>
          View all student intake records for your district. Click &quot;View Forms&quot; to see completed forms for each student.
          Filter by enrollment school year when you have multiple years of data.
        </p>
      </div>

      {/* Counts that double as filters: the daily question is "who still owes paperwork",
          and this answers it in one click instead of opening students one at a time. */}
      <div className="vs-tiles">
        {[
          ['all', 'Students'],
          ['completed', 'Forms complete'],
          ['partial', 'In progress'],
          ['none', 'Not started']
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`vs-tile ${statusFilter === key ? 'is-active' : ''} ${key === 'none' && counts.none > 0 ? 'is-attention' : ''}`}
            onClick={() => setStatusFilter(key)}
            aria-pressed={statusFilter === key}
          >
            <span className="vs-tile-num">{counts[key]}</span>
            <span className="vs-tile-label">{label}</span>
          </button>
        ))}
      </div>

      <div className="vs-controls">
        <input
          type="search"
          className="vs-search"
          placeholder="Search student or parent name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search by student or parent name"
        />
        {yearOptions.length > 0 && (
          <>
            <label className="vs-year-label" htmlFor="view-students-year-filter">School year</label>
            <select
              id="view-students-year-filter"
              className="vs-year"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="">All years</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </>
        )}
        {(search || statusFilter !== 'all') && (
          <button
            type="button"
            className="btn-secondary vs-noprint"
            onClick={() => { setSearch(''); setStatusFilter('all'); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Student Modal */}
      {selectedStudent && (
        <div className="modal-overlay" onClick={() => setSelectedStudent(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Student Intake Information</h3>
              <button onClick={() => setSelectedStudent(null)} className="btn-close">&times;</button>
            </div>
            <div className="modal-body">
              {renderStudentDetails(selectedStudent)}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => handleSoftDelete(selectedStudent.id)}
                className="btn-danger"
              >
                Delete Student Record
              </button>
              <button onClick={() => setSelectedStudent(null)} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Column picker and print, both acting on the list as currently filtered - whichever
          tile, search and year are active - so the four views she named come out of one button
          rather than four. */}
      <div className="vs-toolbar vs-noprint">
        <div className="vs-menu">
          <button type="button" className="btn-secondary" onClick={() => { setShowColumnPicker((v) => !v); setShowPrintPicker(false); }}>
            Columns
          </button>
          {showColumnPicker && (
            <div className="vs-menu-panel" role="group" aria-label="Columns to show">
              {COLUMNS.filter((c) => columnIsAvailable(c) && !c.always).map((col) => (
                <label key={col.key} className="vs-menu-row">
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(col.key)}
                    onChange={() => toggleColumn(col.key)}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="vs-menu">
          <button type="button" className="btn-primary" onClick={() => { showPrintPicker ? setShowPrintPicker(false) : openPrintPicker(); setShowColumnPicker(false); }}>
            Print list
          </button>
          {showPrintPicker && (
            <div className="vs-menu-panel" role="group" aria-label="Columns to print">
              <p className="vs-menu-note">
                Printing {displayedStudents.length} {displayedStudents.length === 1 ? 'student' : 'students'}
                {statusFilter !== 'all' ? ` \u2014 ${STATUS_LABELS[statusFilter] || statusFilter}` : ''}
              </p>
              {COLUMNS.filter((c) => columnIsAvailable(c) && c.printable !== false).map((col) => (
                <label key={col.key} className="vs-menu-row">
                  <input
                    type="checkbox"
                    checked={(printColumns || new Set()).has(col.key)}
                    onChange={() => togglePrintColumn(col.key)}
                  />
                  {col.label}
                </label>
              ))}
              <button
                type="button"
                className="btn-primary vs-menu-go"
                disabled={!printColumns || printColumns.size === 0}
                onClick={() => { setShowPrintPicker(false); setPrinting(true); }}
              >
                Print
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Only on paper. The screen already shows all of this in the tiles and filters; a printed
          sheet that does not say which list it is, or when it was run, is useless a week later. */}
      <div className="vs-print-header">
        <h1>{districtName || 'District'} — Student enrollment status</h1>
        <p>
          {statusFilter === 'all' ? 'All students' : STATUS_LABELS[statusFilter] || statusFilter}
          {' \u00b7 '}{displayedStudents.length} {displayedStudents.length === 1 ? 'student' : 'students'}
          {yearFilter ? ` \u00b7 ${yearFilter}` : ' \u00b7 all school years'}
          {search ? ` \u00b7 matching \u201c${search}\u201d` : ''}
          {' \u00b7 printed '}{new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Students Table */}
      <table className="data-table vs-table">
        <thead>
          <tr>
            {activeColumns.map((col) => (
              col.sortKeyName && !printing
                ? <SortableHeader key={col.key} label={col.label} sortKeyName={col.sortKeyName} columnKey={col.key} />
                : <th key={col.key} className={[col.headerClassName, `vs-col-${col.key}`].filter(Boolean).join(' ')}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedStudents.length === 0 ? (
            <tr>
              <td colSpan={activeColumns.length} style={{ textAlign: 'center' }}>
                {students.length === 0
                  ? 'No student intake records found'
                  : 'No students match these filters'}
              </td>
            </tr>
          ) : (
            displayedStudents.map(student => (
              <tr key={student.id}>
                {activeColumns.map((col) => (
                  <td key={col.key} className={[col.cellClassName, `vs-col-${col.key}`].filter(Boolean).join(' ')}>{col.cell(student)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Student Forms Modal */}
      {showFormsModal && formsModalStudent && (
        <StudentFormsModal
          student={formsModalStudent}
          onClose={() => {
            setShowFormsModal(false);
            setFormsModalStudent(null);
          }}
        />
      )}
    </div>
  );
};

export default ViewStudents;
